import { useRef } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "../../ui/cn";
import { FILTER_LABEL } from "./DateRangeFilter";
import type { ReportGranularity } from "./types";

const OPTIONS: ReportGranularity[] = ["day", "month", "year"];

interface Props {
    value: ReportGranularity;
    onChange: (value: ReportGranularity) => void;
}

/**
 * Day, month or year. A radio group — one choice out of three — drawn as a
 * segmented control the height of the date pickers beside it.
 */
export default function GranularityToggle({ value, onChange }: Props) {
    const { t } = useTranslation();
    const refs = useRef<Record<string, HTMLButtonElement | null>>({});

    const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
        const offset =
            event.key === "ArrowRight" || event.key === "ArrowDown"
                ? 1
                : event.key === "ArrowLeft" || event.key === "ArrowUp"
                  ? -1
                  : 0;
        if (offset === 0) return;
        event.preventDefault();
        const next = OPTIONS[(index + offset + OPTIONS.length) % OPTIONS.length];
        onChange(next);
        refs.current[next]?.focus();
    };

    return (
        <div className="space-y-1.5">
            <span id="reports-granularity" className={cn("block", FILTER_LABEL)}>
                {t("center.reports.groupBy")}
            </span>
            <div
                role="radiogroup"
                aria-labelledby="reports-granularity"
                className="grid h-[42px] grid-cols-3 gap-1 rounded-lg border border-gray-300 bg-white p-1 dark:border-gray-700 dark:bg-gray-900"
            >
                {OPTIONS.map((option, index) => {
                    const selected = option === value;
                    return (
                        <button
                            key={option}
                            ref={(el) => {
                                refs.current[option] = el;
                            }}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            tabIndex={selected ? 0 : -1}
                            onClick={() => onChange(option)}
                            onKeyDown={(event) => handleKeyDown(event, index)}
                            className={cn(
                                "rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
                                selected
                                    ? "bg-brand-500 text-white shadow-theme-xs"
                                    : "text-content-secondary hover:bg-gray-100 hover:text-content-primary dark:hover:bg-white/5"
                            )}
                        >
                            {t(`center.reports.granularity.${option}`)}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
